'use client';

import dynamic from 'next/dynamic';

import Spinner from 'src/components/Spinner';
import { useTranslation } from 'src/i18n/client';

const AnimatedSmiley = dynamic(() => import('src/components/AnimatedSmiley'), {
    ssr: false,
});

type Props = {
    disabled: boolean;
    locale: string;
    items: any[];
    loading: boolean;
    selected: string;
    onSelect: (timeSlot: string) => void;
};

const bgColor = (item: any, selected: any) => {
    if (!item.available) {
        return 'bg-black/10';
    }

    if (item.time === selected) {
        return 'bg-secondary';
    }
    return 'bg-white';
};

export default function TimeSlotsView({ disabled, locale, items, loading, selected, onSelect }: Props) {
    const { t } = useTranslation(locale, 'common');

    if (disabled) return null;

    if (loading) {
        return (
            <div className="flex items-center justify-center">
                <Spinner />
            </div>
        );
    }

    const renderError = (message: string) => (
        <div className="text-center">
            <div className="aspect-1 mx-auto w-1/2 text-red-400">
                <AnimatedSmiley variant="sad" />
            </div>
            {message}
        </div>
    );

    if (!items.length) {
        return renderError(t('appointments.locationClosed'));
    }

    if (!items.some((item) => item.available)) {
        return renderError(t('appointments.noSlotsAvailable'));
    }

    return (
        <div className="grid grid-cols-3 gap-4">
            {items.map((item) => {
                const bg = bgColor(item, selected);
                const weight = item.selected === 'selected' ? 'font-bold' : 'font-normal';
                const decoration = item.available ? 'cursor-pointer no-underline' : 'line-through';
                return (
                    <a
                        key={item.time}
                        className={`border-input-border-color border p-3 text-center ${decoration} ${bg} ${weight}`}
                        onClick={() => {
                            if (item.available) {
                                onSelect(item.time);
                            }
                        }}>
                        {item.time}
                    </a>
                );
            })}
        </div>
    );
}
